/**
 * Mobile Player Notes Tile — DashFooter.dc.html "Player Notes": a BLUE-rule /
 * gold-label header with a More/Less expansion chevron, a vertically-scrollable
 * semi-collapsible body (120px collapsed → 320px expanded, hidden scrollbar), and
 * a 2-line entry per note (colored dot + player·tag, then the note text). Shows
 * notes for rostered players only (excludes former players). All colors via tokens.
 */
import React, { useState, useEffect } from 'react';
import { dynastyTheme } from '../../../services/colorService';
import { leaguesAPI } from '../../../services/apiService';

const PlayerNotesTile = ({ notes = [], leagueId, onSeeAll, onPlayerClick }) => {
  const [rosterMlbIds, setRosterMlbIds] = useState(new Set());
  const [rosterLoaded, setRosterLoaded] = useState(false);
  const [open, setOpen] = useState(false);
  const C = dynastyTheme.tokens.colors;

  // Fetch FRESH roster on mount — exclude former players (accrued_info).
  useEffect(() => {
    if (!leagueId) return;
    leaguesAPI.getMyRosterCanonical(leagueId)
      .then(res => {
        const players = res?.players || [];
        const ids = new Set();
        players.filter(p => !p.accrued_info).forEach(p => {
          const mlbId = Number(p.ids?.mlb || p.player_id);
          if (mlbId) ids.add(mlbId);
        });
        setRosterMlbIds(ids);
        setRosterLoaded(true);
      })
      .catch(() => { setRosterMlbIds(new Set()); setRosterLoaded(true); });
  }, [leagueId]);

  // Don't filter until roster is loaded.
  const rosterNotes = !rosterLoaded ? [] : notes.filter(note => {
    const noteId = Number(note.player_id);
    return noteId && rosterMlbIds.has(noteId);
  });

  // Note dot color by type (DashFooter): injury red / news blue / trade gold / muted.
  const getDot = (noteType) => {
    switch (noteType?.toLowerCase()) {
      case 'injury': return C.error;
      case 'news': return C.blue;
      case 'trade': return C.primary;
      default: return C.muted;
    }
  };
  const tagLabel = (noteType) => {
    switch (noteType?.toLowerCase()) {
      case 'injury': return 'INJ';
      case 'news': return 'NEWS';
      case 'trade': return 'TRADE';
      default: return 'UPD';
    }
  };

  // Left-rule header — BLUE accent bar + gold label (DashFooter) + chevron.
  const Header = ({ withToggle }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 8px' }}>
      <span aria-hidden="true" style={{ width: 3, height: 14, background: C.primary, borderRadius: 2, flexShrink: 0 }} />
      <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: '1px', textTransform: 'uppercase', color: C.primary }}>Player Notes</span>
      {withToggle && (
        <span onClick={() => setOpen(o => !o)} style={{ marginLeft: 'auto', fontSize: '9.5px', fontWeight: 700, color: C.primary, cursor: 'pointer' }}>
          {open ? 'Less ⌃' : 'More ⌄'}
        </span>
      )}
    </div>
  );

  if (!rosterNotes.length) {
    return (
      <div>
        <Header withToggle={false} />
        <p style={{ padding: '8px', color: C.muted, fontSize: '12px' }}>
          {!rosterLoaded ? 'Loading notes…' : 'No recent news for your roster'}
        </p>
      </div>
    );
  }

  return (
    <div>
      <style>{`.notes-scroll::-webkit-scrollbar{height:0;width:0;}`}</style>
      <Header withToggle />
      {/* Semi-collapsible scroll window (DashFooter): 120px → 320px, hidden bar. */}
      <div
        className="notes-scroll"
        style={{ maxHeight: open ? 320 : 120, overflowY: 'auto', transition: 'max-height .2s', scrollbarWidth: 'none', msOverflowStyle: 'none', padding: '0 8px' }}
      >
        {rosterNotes.map((note, idx) => (
          <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '0 1px 6px', borderBottom: `1px solid ${C.hairline}` }}>
            <span aria-hidden="true" style={{ width: 5, height: 5, borderRadius: '50%', background: getDot(note.note_type), marginTop: 5, flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.white }}>
                <span
                  onClick={() => { if (onPlayerClick && note.player_id) onPlayerClick(note.player_id); }}
                  style={{ cursor: onPlayerClick ? 'pointer' : 'default' }}
                >{note.player_name}</span>
                <span style={{ fontSize: 11, color: C.muted, fontWeight: 600, marginLeft: 5 }}>· {tagLabel(note.note_type)}</span>
              </div>
              <div style={{ fontSize: 12, color: C.mutedLight, lineHeight: 1.35 }}>{note.content}</div>
              {/* Line 3 — provenance (ratified restore): source · date. */}
              <div style={{ fontSize: 8, color: C.muted, marginTop: 1 }}>
                {note.source || 'rotowire'}{note.date ? ` · ${note.date}` : ''}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PlayerNotesTile;
